import React, { useState, useEffect } from 'react';
import { useEditorStore } from '../editor-store';
import { generateBlockId } from '../helpers/generate-block-id';
import { Nullable } from 'src/types/nullable.type';
import { EmbedCheckResponse, LinkResponse } from '../links/types';
import {
  LinkBlockContent,
  ImageBlockContent,
  Section,
  TextHorizontalAlignment,
  Block,
  BlockContent,
  TextBlockContent,
} from '../types';
import showdown from 'showdown';
import {
  apiGetEmbedDetailsFromUrl,
  apiJourneyBrainAsk,
  // apiJourneyBrainGeneratePromptResponseByType,
  // apiSearchImageByQuery,
} from 'src/utils/journeyApi';
// import { getDomainBranding } from 'src/utils/branding';
import { MutualActionPlanItem } from 'src/common/interfaces/mutual_action_plan.interface';
import { useMutualActionPlansContext } from '../mutual-action-plans/store';
import { isBlockEmpty, isSectionEmpty } from 'src/document-editor/utils';

export const convertMarkdownToHTML = (markdown: string) => {
  const converter = new showdown.Converter();
  converter.setOption('simpleLineBreaks', true);
  return converter.makeHtml(markdown);
};

export const useJourneyBrain = () => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const setCurrentSectionId = useEditorStore((state) => state.setCurrentSectionId);
  const setScrollToSectionId = useEditorStore((state) => state.setScrollToSectionId);
  const currentSectionId = useEditorStore((state) => state.currentSectionId);
  const sections = useEditorStore((state) => state.layout.sections);
  const [loading, setLoading] = useState(false);
  const [brandfetchResponse, setBrandfetchResponse] = useState<any>(null);
  const [latestAddedSectionId, setLatestAddedSectionId] = useState<Nullable<string>>(null);

  const initMutualActionPlan = useMutualActionPlansContext((state) => state.initMutualActionPlan);

  const blocksRef = React.useRef<Nullable<Record<string, any>>>(null);

  const isBlockCompleted = (blockId: string) => {
    const block = blocksRef.current?.[blockId];
    if (!block) {
      return false;
    }

    return block.completed;
  };

  useEffect(() => {
    if (!latestAddedSectionId) {
      return;
    }

    setCurrentSectionId(latestAddedSectionId);
    setScrollToSectionId(latestAddedSectionId);
  }, [latestAddedSectionId]);

  const addNewBlockToRef = (blockId: string, block: any) => {
    blocksRef.current = {
      ...blocksRef.current,
      [blockId]: block,
    };

    return blockId;
  };

  const dispatchUpdateBlockContent = (
    blockId: string,
    textValue: any,
    horizontalAlignment: TextHorizontalAlignment,
    syncable = false
  ) => {
    dispatchUserEditorAction(
      {
        type: 'update-block-content',
        id: blockId,
        content: { type: 'text', value: textValue, horizontalAlignment: horizontalAlignment },
      },
      { syncable: syncable, undoable: syncable }
    );

    return blockId;
  };

  const setJourneyName = (name: string) => {
    dispatchUserEditorAction(
      {
        type: 'set-journey-name',
        name: name,
      },
      {
        syncable: true,
        undoable: false,
      }
    );
  };

  const isJourneyEmpty = () => {
    return sections.length === 0 || sections.every(isSectionEmpty);
  };

  const dispatchDeleteSection = (sectionId: string) => {
    dispatchUserEditorAction(
      {
        type: 'delete-section',
        id: sectionId,
      },
      {
        syncable: true,
        undoable: false,
      }
    );
  };

  const dispatchAppendSection = (textValue: any, horizontalAlignment: TextHorizontalAlignment) => {
    const blockId = generateBlockId();
    const sectionId = generateBlockId();
    dispatchUserEditorAction({
      type: 'append-section',
      content: { type: 'text', value: textValue, horizontalAlignment: horizontalAlignment },
      id: sectionId,
      blockId: blockId,
    });

    return sectionId;
  };

  const dispatchAppendMutualActionPlan = async (title: string = '', items: string[]) => {
    const blockId = generateBlockId();
    const sectionId = generateBlockId();

    const uuid = generateBlockId().replace(/-/g, '');

    const mutualActionPlanItems = items.map((title, idx) => {
      return {
        uuid: generateBlockId().replace(/-/g, ''),
        title: title,
        description: '',
        due_date: null,
        completed: false,
        assigned_to: [],
        position: idx + 1,
      } as MutualActionPlanItem;
    });

    dispatchUserEditorAction(
      {
        type: 'append-section',
        content: { type: 'mutual-action-plan', uuid: uuid, title: title, items: mutualActionPlanItems },
        id: sectionId,
        blockId: blockId,
        // blockId: blockId,
      },
      {
        syncable: true,
      }
    );

    return sectionId;
  };

  const insertHeadline = (productName: string, headline: string) => {
    // this is the headline
    let value = '';
    if (productName) {
      value += `<h1>${productName}</h1>`;
    }
    if (headline) {
      value += `<h2>${headline}</h2>`;
    }

    setJourneyName(productName);

    // if (currentSectionId) {
    //   dispatchDeleteSection(currentSectionId);
    // }

    // // delete all sections
    // sections.forEach((section) => {
    //   dispatchDeleteSection(section.id);
    // });

    return dispatchAppendSection(value, 'center');
  };

  const insertBusinessDescription = (businessDescription: string) => {
    // this is the business description
    return dispatchAppendSection(`<p>${businessDescription}</p>`, 'left');
  };

  type FeatureBulletPoint = {
    question: string;
    title: string;
    description: string;
  };

  type QuestionAnswer = {
    question: string;
    answer: string;
  };

  const insertFeaturesBulletPoints = (bulletPoints: FeatureBulletPoint[]) => {
    // this is the bullet points
    // add a new line between the question, title and description
    // make the question bold
    const textContent = bulletPoints
      .map(
        (bulletPoint) => `<p><strong>${bulletPoint?.title || ''}</strong></p><p>${bulletPoint?.description || ''}</p>`
      )
      .join('');

    return dispatchAppendSection(textContent, 'left');
  };

  const insertProspectiveQuestionAnswers = (questionAnswers: QuestionAnswer[]) => {
    // this is the question answers
    // add a new line between the question and answer
    // make the question bold
    const textContent = questionAnswers
      .map(
        (questionAnswer) =>
          `<p><strong>${questionAnswer.question || ''}</strong></p><p>${questionAnswer.answer || ''}</p>`
      )
      .join('');

    return dispatchAppendSection(textContent, 'left');
  };

  const insertChecklistItems = (checklistItems: string[]) => {
    return dispatchAppendMutualActionPlan('Action items', checklistItems);
  };

  const insertUrl = async (url: string, sectionId: string) => {
    const response: EmbedCheckResponse = await apiGetEmbedDetailsFromUrl(url);
    const linkResponse: LinkResponse = {
      url: response.url,
      transformed_url: response.transformed_url,
      title: response.oembed.title,
      thumbnail: {
        url: response.oembed?.thumbnail_url || null,
        width: response.oembed?.thumbnail_width || null,
        height: response.oembed?.thumbnail_height || null,
      },
      destination: response.destination,
      iframe_safe: response.iframe_safe,
      third_party_iframe_safe: response.third_party_iframe_safe,
    };

    const displayOption = response.iframe_safe ? 'inline' : 'new-tab';
    const blockId = generateBlockId();

    const content = {
      type: 'link',
      link: linkResponse,
      displayOption,
    } as LinkBlockContent;

    dispatchUserEditorAction({
      type: 'append-block',
      sectionId: sectionId,
      content: content,
      id: blockId,
      layoutExtraParams: {
        aspectRatio: {
          width: 1.77,
          height: 1,
        },
      },
    });

    return blockId;
  };

  const insertImage = (url: string, urls: [], sectionId: string) => {
    const blockId = generateBlockId();

    const content = {
      type: 'image',
      url: url,
      objectFit: 'cover',
      ...(urls && { dalleParams: { urls: urls } }),
    } as ImageBlockContent;

    dispatchUserEditorAction({
      type: 'append-block',
      sectionId: sectionId,
      content: content,
      id: blockId,
      layoutExtraParams: {},
    });
  };

  const insertWrapUpCallToAction = (callToAction: string) => {
    // this is the call to action
    const textContent = `<h2>${callToAction}</h2>`;

    return dispatchAppendSection(textContent, 'center');
  };

  const insertSlide = (content: string) => {
    const sectionId = dispatchAppendSection(content, 'left');
    setLatestAddedSectionId(sectionId);
    return sectionId;
  };

  // const generateImageSearchQuery = async (query: string) => {
  //   try {
  //     const r = await apiJourneyBrainGeneratePromptResponseByType('generate_image_search_query', { query });
  //     const { response } = r;
  //     const searchQuery = response;

  //     if (searchQuery && searchQuery !== 'NONE') {
  //       const r = await apiSearchImageByQuery(searchQuery, 4);
  //       const { urls } = r;
  //       return urls;
  //     }

  //     return [];
  //   } catch (e) {
  //     console.log(e);
  //     return [];
  //   }
  // }

  const generatePitchForTopic = (topic: string) => {
    setLoading(true);
    apiJourneyBrainAsk('pitch_for_topic', {
      topic,
    }).then((r) => {
      const { response } = r;
      const { slides } = response;

      slides.forEach((slide: any) => {
        // slide has 2 keys, headline and sentences
        let { headline, description } = slide;

        // trim the newline from the beginning and end of the headline
        // description is a markdown string
        headline = headline.replace(/^\n|\n$/g, '');
        description = description.replace(/^\n|\n$/g, '');

        // sentences is a markdown string
        const sentencesHTML = convertMarkdownToHTML(description);

        const html = `<h2>${headline}</h2>${sentencesHTML}`;

        insertSlide(html);
      });

      setLoading(false);
    });
  };

  const generateSlideForTopic = (topic: string) => {
    setLoading(true);
    apiJourneyBrainAsk('slide_for_topic', { topic })
      .then((r) => {
        let { response, response_format } = r;
        // sentences is a markdown string
        if (response_format === 'plain_text') {
          response = response.replace(/^\n|\n$/g, '');
          const html = convertMarkdownToHTML(response);
          insertSlide(html);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const generatePitchForWebsite = (params: any = {}, onDone?: () => void) => {
    const { url, prospect_url, is_gpt_4 } = params;

    let currentText = '';
    let parts: any = {};

    // partBlockIdMap is a map of part1, part2, part3, part4, part5, part6, part7, part8 to blockId
    let partBlockIdMap: any = {};
    setLoading(true);

    const isPartCompleted = (part: string) => {
      return partBlockIdMap[part]?.completed || false;
    };

    const completePart = (part: string) => {
      if (!isPartCompleted(part)) {
        partBlockIdMap[part] = {};
        partBlockIdMap[part].completed = true;
      }
    };

    const getPartBlockId = (part: string) => {
      return partBlockIdMap[part]?.blockId || null;
    };

    const setPartSectionId = (part: string, blockId: string) => {
      if (!partBlockIdMap[part]) {
        partBlockIdMap[part] = {
          blockId: null,
          completed: false,
        };
      }
      partBlockIdMap[part].blockId = blockId;
    };

    const brainAction = prospect_url ? 'pitch_for_prospect' : 'pitch_for_website';
    apiJourneyBrainAsk(brainAction, {
      url,
      ...(prospect_url ? { prospect_url } : {}),
      model: is_gpt_4 ? 'gpt-4' : 'gpt-3.5-turbo',
    }).then(async (r) => {
      const { response, prompt } = r;
      console.log('*****PROMPT*****', prompt);
      console.log('*****RESPONSE*****', response);
      const { part1, part2, part3, part4, part5, part6, part7, part8 } = response;

      insertHeadline(part1 || '', part3 || '');
      let sectionIdToDelete = null;
      if (currentSectionId && isJourneyEmpty()) {
        sectionIdToDelete = currentSectionId;
      }

      if (sectionIdToDelete) {
        dispatchDeleteSection(sectionIdToDelete);
      }

      const sectionId2 = insertBusinessDescription(part4 || '');

      // generateImageSearchQuery(part4).then((imageUrls) => {
      //   if (imageUrls && imageUrls.length > 0) {
      //     const imageUrl = imageUrls[0];
      //     insertImage(imageUrl, imageUrls, sectionId2);
      //   }
      // });

      insertFeaturesBulletPoints(part5);
      insertProspectiveQuestionAnswers(part6);
      insertChecklistItems(part7);

      const sectionId6 = insertWrapUpCallToAction(part8);
      insertUrl(url, sectionId6);

      onDone && onDone();
    });
  };

  return {
    loading,
    generatePitchForWebsite,
    generatePitchForTopic,
    generateSlideForTopic,
  };
};
